<script module>
  // Set once the island has mounted in this tab. It mounts again with every page, and the menu
  // drops in only when the site opens: on a page change it holds still while the pages cross-fade.
  let intro_played = false;
</script>

<script>
  import { ENTRY_TOOLS, BLOCKS } from '#lib/app_config.js';
  import { 
    is_cursor_at_start, 
    is_cursor_at_end, 
    focus_start_of_next_text_node, 
    focus_end_of_previous_text_node,
    has_access,
    fetch_json,
    format_date, 
    generate_css_variables,
    render_micro_markdown,
  } from '#lib/util.js';
  import { write_blocks_to_clipboard } from '#lib/block_clipboard.js';
  import { getContext } from 'svelte';
  import { goto, afterNavigate } from '$app/navigation';
  import { page } from '$app/state';
  import { lazy } from '#lib/lazy.svelte.js';
  import AddAfterTool from '#lib/AddAfterTool.svelte';
  import BlockGrid from '#lib/BlockGrid.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import CancelTool from '#lib/CancelTool.svelte';
  import CreateEntryTool from '#lib/CreateEntryTool.svelte';
  import Divider from '#lib/Divider.svelte';
  // import LinkTool from '#lib/LinkTool.svelte';
  import Navigation from '#lib/Navigation.svelte';
  import Popover from '#lib/Popover.svelte';
  import SaveTool from '#lib/SaveTool.svelte';
  import MediaActivityChip from '#lib/MediaActivityChip.svelte';
  import StartEditingTool from '#lib/StartEditingTool.svelte';
  // Editing-only, and it pulls in LayoutThumbnail (an SVG per layout) - loaded on first use so
  // readers never download it
  const load_layout_selection_tool = () => import('#lib/LayoutSelectionTool.svelte');
  // Crop mode of an image entry: editing-only, prefetched by ImageEntry when editing starts
  const image_crop_tools = lazy(() => import('#lib/ImageCropTools.svelte'));
  import { ENTRIES } from '#lib/app_config.js';
  import t from '#lib/i18n.svelte.js';
  import IconHamburger from '#lib/IconHamburger.svelte';
  import CollectionTool from '#lib/CollectionTool.svelte';
  import { cycle_layout } from "#lib/StoryLayouts.js";
  import VerticalDivider from '#lib/VerticalDivider.svelte';
  import AccountMenu from '#lib/AccountMenu.svelte';
  import AnalyticsWidget from '#lib/AnalyticsWidget.svelte';
  import FieldVisibilityMenu from './FieldVisibilityMenu.svelte';
  import Dropdown from '#lib/Dropdown.svelte';
  import Dialog from '#lib/Dialog.svelte';

  // IMPORTANT: With the current design, we need to make DynamicIsland work outside of entries too,
  // so entry_api might be undefined and must be guarded.
  let entry_api = getContext('entry_api');

  let {
    site_settings,
    /** @type {SvelteSnippet} add a snippet that's in front of row_1_right items */
    row_1_right_start = undefined,
    /** @type {SvelteSnippet} add a snippet that's displayed instead of row_1_right contents */
    row_1_right = undefined,
    /** @type {SvelteSnippet} add a snippet that's displayed instead of row_2 contents */
    row_2 = undefined,
    /** @type {boolean} if true, the dynamic island will be displayed in editing mode */
    is_editing = false
  } = $props();

  let current_href = page.params.entry ? '/' + page.params.entry : page.route.id ?? '';
  let mobile_drawer_is_open = $state(false);
  // Page intro (styles): the menu drops in on the first page of a visit only
  const intro = !intro_played;
  $effect(() => { intro_played = true; });
  let desktop_ellipsis_menu_anchor_ref = $state();
  let mobile_ellipsis_menu_anchor_ref = $state();
  let embed_create_anchor = $state();
  let block = $derived(entry_api?.active_container?.blocks[entry_api?.active_container?.selected_index]);

  // Get creatable entry types for embed popover
  const creatable_entry_types = Object.entries(ENTRIES).filter(
    ([key, config]) => key !== 'collection'
  );

  /**
   * Create a new entry and open it for editing
   * @param {string} type
   */
  async function create_embed_entry(type) {
    try {
      const new_entry = await fetch_json('POST', '/api/create-entry', {
        type,
        visibility: 'public', // Match non-contextual flow - user can change visibility if needed
      });
      
      entry_api?.start_contextual_editing(new_entry.entry_id, {
        onsave: (saved_entry_id) => {
          if (block) {
            block.entry_previews_multiref = [
              ...(block.entry_previews_multiref || []),
              saved_entry_id
            ];
          }
        },
        oncancel: async (cancelled_entry_id) => {
          try {
            await fetch_json('POST', '/api/delete-entry', { entry_id: cancelled_entry_id });
          } catch (err) {
            console.error('Failed to delete cancelled entry:', err);
          }
        }
      });
    } catch (err) {
      console.error('Failed to create entry:', err);
    }
  }

  /**
   * Open entry picker drawer to add from library
   */
  function open_library_picker() {
    entry_api?.open_entry_picker({
      entry_type: '',
      // Media is what gets embedded most, so the picker opens on that tab —
      // every other type is one tab click away.
      initial_entry_type: 'image',
      initial_selected: [],
      excluded_ids: block?.entry_previews_multiref || [],
      onconfirm: (selected_ids) => {
        if (block) {
          block.entry_previews_multiref = [
            ...(block.entry_previews_multiref || []),
            ...selected_ids
          ];
        }
      },
    });
  }


  let is_admin = $derived.by(() => {
    return page.data.current_user?.role === 'admin'
  });

  let is_member = $derived.by(() => {
    return page.data.current_user?.role === 'member'
  });

  // Admins edit any entry, members only their own collections (as the server allows)
  let can_start_editing = $derived(!!entry_api?.entry && (is_admin || (
    is_member
    && entry_api.entry.type === 'collection'
    && entry_api.entry.user_id === page.data.current_user.user_id
  )));

  // Fetch the editing-only chunks once the page is idle, so they are cached by the time an
  // admin enters edit mode and nothing pops in late. Readers never trigger this.
  $effect(() => {
    if (!is_admin) return;
    const idle = window.requestIdleCallback ?? (callback => setTimeout(callback, 1500));
    idle(() => load_layout_selection_tool());
  });

  let can_add_to_collection = $derived.by(() => {
    return (is_admin || is_member) && entry_api && !entry_api?.is_editing
  });


  // Not after a shallow navigation: that is the drawer itself opening
  afterNavigate(({ shallow }) => {
    if (shallow) return;
    if (mobile_drawer_is_open) {
      mobile_drawer_is_open = false;
    }
  });

  async function delete_entry() {
    const entry_id = entry_api.entry.entry_id;
    const entry_type = entry_api.entry.type;

    const has_access_to_all =  is_admin || has_access(site_settings.min_access_role_for_all_module, page.data?.current_user?.role)

    if (!(is_admin || (is_member && entry_type === 'collection'))) return;

    const is_series_base = entry_type === 'event'
      && entry_api.entry.series_id
      && entry_api.entry.series_index === 0;

    const confirmation_message = is_series_base
      ? t('confirm_deletion_of_series_base_event')
      : t(`confirm_deletion_of_${entry_type}`);

    if (confirm(confirmation_message)) {
      await fetch_json('POST', '/api/delete-entry', { entry_id, entry_type });
      if (has_access_to_all) {
        goto('/all'); // navigate to all
      } else {
        goto('/'); // navigate to home
      } 
    }
  }

  // Series delete state
  let show_series_delete_dialog = $state(false);
  let series_delete_impact = $state(null);
  let is_loading_series_impact = $state(false);
  let is_series_deleting = $state(false);
  let series_delete_notify = $state(false);

  async function open_series_delete_dialog() {
    show_series_delete_dialog = true;
    is_loading_series_impact = true;
    series_delete_notify = false;
    try {
      series_delete_impact = await fetch_json('POST', '/api/series-impact', {
        series_id: entry_api.entry.series_id,
        from_entry_id: entry_api.entry.entry_id,
      });
    } catch (err) {
      console.error('Failed to load series impact:', err);
      series_delete_impact = null;
    }
    is_loading_series_impact = false;
  }

  async function handle_delete_series(mode) {
    is_series_deleting = true;
    try {
      const result = await fetch_json('POST', '/api/delete-series-entries', {
        series_id: entry_api.entry.series_id,
        from_entry_id: entry_api.entry.entry_id,
        mode,
        notify_registrants: series_delete_notify,
      });
      show_series_delete_dialog = false;
      // The current event may have been deleted, so navigate away
      // (mode 'empty_only' might skip the current event if it has registrations)
      if (mode === 'all' || (result && result.deleted_count > 0)) {
        goto('/');
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error('Failed to delete series entries:', err);
      alert(err.message);
    }
    is_series_deleting = false;
  }

  // IMPORTANT: All global keybindings must go here.
  // We can't do multiple <svelte:window {onkeydown} /> because that
  // somehow retriggers onkeydown handlers inside (like inside <PlainText>)
  // TODO: We should soon modularize this and create a dedicated place for
  // keyboard handling, including defining shortcuts declaratively.
  async function onkeydown(e) {
    if (entry_api?.is_disabled) return; // no nothing;

    const active_container = entry_api?.active_container;
    const selected_block = entry_api?.active_container?.selected_block;
    const is_container_selection = entry_api?.active_container?.selection?.type === 'container';
    const is_field_selection = entry_api?.active_container?.selection?.type === 'field';

    if (entry_api?.is_editing) {
      if (e.key === 's' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        e.stopPropagation();
        entry_api.save();
      } else if (e.key === 'ArrowDown' && (e.metaKey || e.ctrlKey) && is_container_selection) {
        active_container.move_block('down');
        e.preventDefault();
        e.stopPropagation();
      } else if (e.key === 'ArrowUp' && (e.metaKey || e.ctrlKey) && is_container_selection) {
        active_container.move_block('up');
        e.preventDefault();
        e.stopPropagation();
      } else if (e.key === 'Escape' && (e.metaKey || e.ctrlKey)) {
        entry_api.cancel_editing();
      } else if (e.key === 'Enter' && !e.metaKey && !e.shiftKey) {
        if ((is_container_selection && selected_block) || (is_field_selection && is_cursor_at_end(document.activeElement))) {
          const new_block_type = BLOCKS[active_container.selected_block?.type].insert_after_block_type;
          // Ihis will add a new block with sensible defaults and update the selection
          // It will also inherit properties from the predecessor block.
          await active_container.add_block(new_block_type, { description: ''});
        }
        // We never allow enter to create a new line in a field.
        // (shift+enter) is allowed (but not recommended).
        e.preventDefault();
        e.stopPropagation();
      } else if (e.key === 'Escape' && selected_block) {
        // First time you press ESC we clear the text selection.
        // Second time we clear the container block selection as well.
        if (document.activeElement !== document.body && active_container.selection.type === 'field') {
          document.getSelection()?.empty();
          document.activeElement.blur();
          // TODO: Can we instead somehow detect focus blur and update the 
          // selection in the Container's handle_selection_change?
          active_container.set_selection({
            type: 'container',
            block_indexes: [active_container.selection.block_index],
            block_index_achor: active_container.selection.block_index
          });
        } else {
          active_container.clear_selection();
        }
      } else if (e.key === 'Backspace' && (e.metaKey || e.ctrlKey) && is_container_selection) {
        active_container.delete_block();
      } else if (e.key === 'Backspace' && !e.metaKey && !e.ctrlKey && !e.shiftKey && is_field_selection) {
        if (active_container?.ref?.is_at_start_of_first_field(active_container?.selected_index, document.activeElement)) {
          if (active_container?.ref?.is_block_empty(active_container?.selected_index)) {
            active_container.delete_block(active_container?.selected_index);
          } else if (active_container?.selected_index > 0) {
            // Focus end of previous field
            active_container?.ref?.focus_block(active_container?.selected_index - 1, 'end');
          }
          e.preventDefault();
          e.stopPropagation();
        } else {
          // Run standard backspace behavior
        }
      } else if (e.key === 'ArrowDown' && e.shiftKey && is_container_selection) {
        // NOTE: This is skipped when there is an active text selelection
        active_container.expand_selection('down');
        e.preventDefault();
        e.stopPropagation();
      } else if (e.key === 'ArrowUp' && e.shiftKey && is_container_selection) {
        active_container.expand_selection('up');
        e.preventDefault();
        e.stopPropagation();
      } else if (['ArrowRight','ArrowDown'].includes(e.key) && is_field_selection && is_cursor_at_end(document.activeElement)) {
        e.preventDefault();
        e.stopPropagation();
        focus_start_of_next_text_node();
      } else if (e.key === 'ArrowDown' && is_container_selection) {
        active_container?.select_next_block();
        e.preventDefault();
        e.stopPropagation();
      } else if (['ArrowLeft','ArrowUp'].includes(e.key) && is_field_selection && is_cursor_at_start(document.activeElement)) {
        e.preventDefault();
        e.stopPropagation();
        focus_end_of_previous_text_node();
      } else if (e.key === 'ArrowUp' && is_container_selection) {
        active_container?.select_previous_block();
        e.preventDefault();
        e.stopPropagation();
      } else if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && is_container_selection && selected_block) {
        const direction = e.key === 'ArrowRight' ? 'next' : 'previous';
        selected_block.layout = cycle_layout(selected_block.layout, direction);
        e.preventDefault();
        e.stopPropagation();
      } else if (e.key === 'd' && (e.metaKey || e.ctrlKey) && is_container_selection) {
        // Prevent default MUST be called before any other code to ensure browser bookmark action is blocked
        e.preventDefault();
        e.stopPropagation();
        active_container.duplicate_selected_blocks();
      } else if (['c', 'x'].includes(e.key) && (e.metaKey || e.ctrlKey) && is_container_selection) {
        // Copy or cut the selected blocks, so they can be pasted onto another page (see
        // block_clipboard.js and Entry.svelte's handle_window_paste).
        // A block selection only tells us no text is selected inside the container - text can
        // still be selected elsewhere on the page, and there the browser's own copy must win.
        if (document.getSelection()?.isCollapsed === false) return;
        const blocks = active_container.copy_selected_blocks();
        if (!blocks) return;
        e.preventDefault();
        e.stopPropagation();
        write_blocks_to_clipboard(blocks, { source_entry_id: entry_api?.entry?.entry_id });
        if (e.key === 'x') active_container.delete_block();
      }
    } else {
      if (e.key === 'e' && (e.metaKey || e.ctrlKey) && can_start_editing) {
        e.preventDefault();
        entry_api.start_editing();
      }
    }
  }
</script>

<!--------------------------- SNIPPETS START -------------------------->

{#snippet entry_tools()}
  {#if entry_api?.is_editing && entry_api.entry.crop_session}
    {#if image_crop_tools.component}
      <image_crop_tools.component session={entry_api.entry.crop_session} part='format' />
    {/if}
  {:else if entry_api?.is_editing && ENTRY_TOOLS[entry_api.entry.type]}
    {#each ENTRY_TOOLS[entry_api.entry.type] as tool, i}
      {#if (entry_api?.active_container?.selected_index === undefined || !tool.hide_on_block_selection)}
        {@const ToolComponent = tool.component}
        <ToolComponent entry={entry_api.entry} is_editing={entry_api.is_editing} />
      {/if}
    {/each}
  {/if}
{/snippet}

<!-- Cancel/Save, or the crop mode's own pair while an image is being cropped -->
{#snippet commit_tools()}
  {#if entry_api.entry.crop_session}
    {#if image_crop_tools.component}
      <image_crop_tools.component session={entry_api.entry.crop_session} part='commit' />
    {/if}
  {:else}
    <CancelTool />
    <SaveTool />
  {/if}
{/snippet}

<!-- Move tool removed - move controls are now in BlockMoveHandle on the canvas -->

{#snippet layout_selection_tool()}
  {#if 
    block 
    // && block.type === 'story' && entry_api?.active_container?.selected_block
  }
    {#await load_layout_selection_tool() then { default: LayoutSelectionTool }}
      <LayoutSelectionTool {block} />
    {/await}
  {/if}
{/snippet}


{#snippet embed_tools()}
  {#if ['story'].includes(block?.type) && !entry_api?.is_contextual_editing}
    <!-- One menu for putting entries into the block: an existing one, or a new one of a type -->
    <Popover
      id="embed-create-popover"
      anchor={embed_create_anchor}
      placement="bottom-start"
      class="normal"
    >
      <div class="embed-create-menu flex-column">
        <button onclick={(e) => { e.currentTarget.closest('[popover]')?.hidePopover?.(); open_library_picker(); }}>
          <ButtonLabel
            variant='ghostly'
            start_icon='search'
            align='start-stretch'
            text_style='text-control'
          >
            {t('from_library')}
          </ButtonLabel>
        </button>
        <p class="embed-create-heading text-caption text-2">{t('new_entry_of_type')}</p>
        {#each creatable_entry_types as [type, config]}
          <button onclick={() => create_embed_entry(type)}>
            <ButtonLabel 
              variant='ghostly' 
              start_icon={config.icon}
              align='start-stretch'
              text_style='text-control'
            >
              {t(config.label_i18n)}
            </ButtonLabel>
          </button>
        {/each}
      </div>
    </Popover>

    <button 
      bind:this={embed_create_anchor}
      popovertarget="embed-create-popover"
    >
      <ButtonLabel 
        variant='ghostly text-compact' 
        end_icon='small-arrow-down'
        text_style='text-control'
      >
        {t('insert')}
      </ButtonLabel>
    </button>
  {/if}
{/snippet}

{#snippet story_block_visible_fields_tool()}
  {#if block && block.type === 'story' && entry_api?.active_container?.selected_block}
    <Dropdown
      variant='ghostly text-compact'
      text_style='text-control'
      label={t('fields')}
      expected_width={240}
    >
      <FieldVisibilityMenu context="block" {block} />
    </Dropdown>
  {/if}
{/snippet}

{#snippet contextual_field_selection_tools()}
  <!-- EXPERIMENTAL: Contextual field selection tools -->
  <!-- {#if entry_api?.active_container?.selection?.type === 'field' 
    && ['primary_link_title', 'secondary_link_title'].includes(entry_api?.active_container?.selection?.field_name)
    && block.show_links
  } -->
    <!-- HACK: we assume that xxx_title has a corresponding xxx_href -->
    <!-- <LinkTool
      {block}
      href_prop={entry_api?.active_container?.selection?.field_name.replace('_title', '_href')}
    /> -->
  <!-- {/if} -->
{/snippet}

{#snippet delete_entry_tool()}
{#if (
  entry_api && !entry_api?.is_editing &&
  (
    is_admin || 
    page.data.current_user?.user_id === entry_api?.entry?.user_id
  )
)}
  <button onclick={delete_entry} class='delete-entry-button'>
      <ButtonLabel 
        variant='brand ghostly text-compact'
        start_icon='trash' 
        align='start-stretch'
        text_style='text-control'
      >
        {t('delete')}
      </ButtonLabel>
    </button>
  {/if}
{/snippet}

{#snippet delete_series_tool()}
  {#if is_admin && entry_api && !entry_api?.is_editing && entry_api?.entry?.series_id}
    <button onclick={open_series_delete_dialog}>
      <ButtonLabel
        variant='brand ghostly text-compact'
        start_icon='trash'
        align='start-stretch'
        text_style='text-control'
      >
        {entry_api.entry.series_index === 0
          ? t('delete_entire_series')
          : t('delete_this_and_future_events')}
      </ButtonLabel>
    </button>
  {/if}
{/snippet}

{#snippet duplicate_entry_tool()}
  <!-- No duplicate for media entries: the library is the reuse mechanism —
       a clone would be a second entry pointing at the same asset -->
  {#if is_admin && entry_api && !entry_api?.is_editing && entry_api.entry.type !== 'image'}
    <a href={`/new/${entry_api.entry.type}?from=${entry_api.entry.entry_id}`} class='duplicate-entry-link'>
      <ButtonLabel 
        variant='brand ghostly text-compact'
        start_icon='duplicate' 
        align='start-stretch'
        text_style='text-control'
      >
        {t('duplicate')}
      </ButtonLabel>
    </a>
  {/if}
{/snippet}

{#snippet add_to_collection_tool()}
  {#if can_add_to_collection}
    <!-- {#if entry_api?.entry?.type !== 'collection'} -->
    <button onclick={() => { entry_api.add_to_collection_dialog_open = true; }}>
      <ButtonLabel 
        variant='brand ghostly text-compact'
        start_icon='bookmark' 
        align='start-stretch'
        text_style='text-control'
      >
        {t('save_to_collection')}
      </ButtonLabel>
    </button>
    <!-- {/if} -->
  {/if}
{/snippet}

{#snippet updated_at_tool()}
  <ButtonLabel 
    variant='brand ghostly text-compact' 
    start_icon='calendar' 
    align='start-stretch'
    text_style='text-control'
  >
    <span class='text-3'>{t('updated_at')} {format_date(entry_api?.entry?.updated_at, 'YDMMMHms')}</span>
  </ButtonLabel>
{/snippet}

{#snippet entry_analytics_tool(show_divider)}
  {#if is_admin && entry_api?.entry && !entry_api?.is_editing && page?.data?.entry_analytics}
    {#if show_divider}
    <Divider class='px p-4' label={t('analytics')} />
    {/if}
    
    <AnalyticsWidget 
      entry_analytics={page.data.entry_analytics}
      site_analytics={page.data.site_analytics}
    />
  {/if}
{/snippet}


<!-- TODO: Add Link to "Account Settings" -->
<!-- TODO: Add Link to "My Collection" -->

<!-------------------------- SNIPPETS END -------------------------->



<!-- svelte-ignore a11y_interactive_supports_focus -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<div 
  class='dynamic-island-backdrop' 
  class:visible={mobile_drawer_is_open}
  onclick={()=>{mobile_drawer_is_open = false;}}
  role='button'
></div>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- The dynamic-island id is used for event handling Container.svelte -->
<header
  id='dynamic-island'
  class='dynamic-island {site_settings.menu_placement} menu-variant'
  style='{generate_css_variables(site_settings.menu_background)}'
  class:has-background={site_settings.menu_background !== 'transparent'}
  class:is-editing={entry_api?.is_editing}
  class:contextual-editing-active={entry_api?.is_disabled}
  class:mobile-drawer-open={mobile_drawer_is_open}
  class:intro
>
  <BlockGrid>
    <div class='surface'>
      <section class='row-1'>
        <div class='row-1-left hide-scrollbar'>
          {#if !entry_api?.is_editing}
            <Navigation
              id='topbar-nav'
              orientation='row'
              text_style='text-control'
              nav_items={site_settings.nav_items}
              current={current_href}
              {is_editing}
              align={site_settings.menu_mobile_alignment}
            />
          {/if}
          {@render entry_tools()}
        </div>

        {#if page.data.current_user}
          <div class='row-1-left-mobile flex-row flex-wrap full-width'>
            <VerticalDivider small={true} />
          </div>
        {/if}
        
        <div class='row-1-center'>
          <!-- EDITING START -->
          {#if entry_api?.is_editing}
            <!-- Block Tools Group (the rule before it is drawn by .row-1-center::before) -->
            {#if block}
              <AddAfterTool />
            {/if}

            {@render layout_selection_tool()}

            {@render story_block_visible_fields_tool()}

            {@render embed_tools()}

            {@render contextual_field_selection_tools()}

            {#if entry_api?.entry?.type === 'collection' && is_admin}
              <CollectionTool entry={entry_api?.entry} />
            {/if}

          {/if}
        </div>
        
        <div class='row-1-right'>
          {#if row_1_right_start}
            {@render row_1_right_start()}
          {/if}
          {#if row_1_right}
            {@render row_1_right()}
          {:else}

            <!-- Search / Browse All -->
            {#if 
              !entry_api?.is_editing 
              && has_access(site_settings.min_access_role_for_all_module, page.data?.current_user?.role)
            }
              <a
                class='explore-link' 
                href='/all' 
                style='--button-outer-block-spacing: var(--default-button-outer-block-spacing)'
                title={t('browse')}
              >
                <ButtonLabel variant='brand flush text-compact' start_icon='search'>
                  {t('contents')}
                </ButtonLabel>
              </a>
            {/if}
          

            <!-- Tool hint for users to select a block to edit -->
            {#if entry_api?.is_editing && entry_api?.active_container?.selection === undefined && entry_api?.entry?.type === 'page'}
              <button class='px py p-2'>
                <ButtonLabel hover_effect={false} align='end' variant='brand flush text-compact'>
                  {t('select_a_block_to_edit')}
                </ButtonLabel>
              </button>
            {/if}

            <!-- Media activity: uploads in flight / optimizing / failed -->
            <MediaActivityChip />

            <!-- Save / Cancel Tool -->
            {#if entry_api?.is_editing}
              <div class='save-cancel-tool flex-row items-stretch'>
                {@render commit_tools()}
              </div>
            {/if}
            
            <!-- Create Entry Tool -->
            {#if !entry_api?.is_editing}
              {#if is_admin}
                <CreateEntryTool class='create-entry-tool' {site_settings} />
              {/if}
              {#if
                 entry_api && is_admin ||
                // allow members to edit their own collections
                 is_member && entry_api?.entry?.type === 'collection' &&
                 entry_api?.entry?.user_id === page.data.current_user?.user_id
              }
                <StartEditingTool class='desktop-start-editing-tool' />
              {/if}
            {/if}

            <!-- Ellipsis menu: hide button if the popover is empty -->
            {#if  
              !entry_api?.is_editing 
              && page.data.current_user
              && can_add_to_collection
            }
              <button
                bind:this={desktop_ellipsis_menu_anchor_ref} 
                class='desktop-ellipsis-menu-button'
                style='--button-outer-block-spacing: var(--default-button-outer-block-spacing)'
                popovertarget='desktop_ellipsis_menu_popover' 
                
                title={t('settings')}
              >
                <ButtonLabel variant='brand flush text-compact' start_icon='dot-dot-dot' />
              </button>
              
              <Popover
                anchor={desktop_ellipsis_menu_anchor_ref}
                id='desktop_ellipsis_menu_popover'
                placement='bottom-end'
                class='normal py p-3'
                offset={6}
              >
                <div class='flex-column'>
                  {#if entry_api?.entry}
                    <Divider class='px p-4' label={t(entry_api?.entry?.type)} />
                    {@render delete_entry_tool()}
                    {@render delete_series_tool()}
                    {@render duplicate_entry_tool()}
                    {@render add_to_collection_tool()}
                    {@render updated_at_tool()}
                    {@render entry_analytics_tool(true)}
                  {/if}
                </div>
              </Popover>
            {/if}

            <!-- Account Menu -->
            {#if page.data.current_user && !entry_api?.is_editing}
              <AccountMenu />
            {/if}

            
          {/if}
        </div>
          
        <!-- Hamburger Menu -->
        {#if !entry_api?.is_editing}
          <button 
            class='mobile-drawer-toggle'
            onclick={()=>{mobile_drawer_is_open = !mobile_drawer_is_open}}
            style='--button-outer-block-spacing: var(--default-button-outer-block-spacing)'
            aria-label={t('toggle_navigation')}
          >
            <ButtonLabel variant='brand flush text-compact'>
              {#snippet start_icon()}
                <IconHamburger toggle={mobile_drawer_is_open} />
              {/snippet}
            </ButtonLabel>
          </button>
        {/if}


        <!-- Must be inside header:
          - because Safari doesn't support position: fixed for bottom screen aligned elements 
          - because relies on container query for visiblity, and the container that is queried is row-1
        -->
        {#if is_admin && entry_api?.entry}
          <div
            class='mobile-entry-editing-tools flex-row {site_settings.menu_placement} menu-variant'
            class:mobile-drawer-open={mobile_drawer_is_open}
            bind:this={mobile_ellipsis_menu_anchor_ref} 
          >
            {#if !entry_api?.is_editing}
              <StartEditingTool class='mobile-start-editing-tool' />
              <button
                class='mobile-ellipsis-menu-button'
                popovertarget='mobile_ellipsis_menu_popover' 
                
                title={t('settings')}
              >
                <ButtonLabel variant='brand flush text-compact' start_icon='dot-dot-dot' />
              </button>
              
              <Popover
                anchor={mobile_ellipsis_menu_anchor_ref}
                id='mobile_ellipsis_menu_popover'
                placement='top'
                class='normal py p-3'
              >
                <div class='flex-column'>
                  <Divider class='px p-4' label={t(entry_api?.entry?.type)} />
                  {@render delete_entry_tool()}
                  {@render delete_series_tool()}
                  {@render duplicate_entry_tool()}
                  {@render add_to_collection_tool()}
                  {@render updated_at_tool()}
                  {@render entry_analytics_tool(true)}
                </div>
              </Popover>
            {/if}
            {#if entry_api?.is_editing}
              {@render commit_tools()}
            {/if}

          </div>
        {/if}

      </section>

      {#if row_2}
        <section class='row-2'>
          {@render row_2()}
        </section>
      {/if}

      <section
        class='mobile-drawer flex-column full-height items-{site_settings.menu_mobile_alignment}'
        class:open={mobile_drawer_is_open}
      >
        <Navigation
          id='mobile-nav'
          text_style='text-control'
          orientation='column'
          nav_items={site_settings.nav_items}
          current={current_href}
          align={site_settings.menu_mobile_alignment}
          class='full-height mobile-nav-section'
          text_size='text-large'
        />
        {#each [1, 2, 3, 4, 5] as index}
          {#if site_settings[`footer_nav_${index}_show_in_mobile_drawer`]}
            {#if site_settings[`footer_nav_${index}_title`]}
              <ButtonLabel 
                variant='brand ghostly text-compact'
                class='nav-item footer-nav-group-title'
                text_style={'text-preamble'}
                align={site_settings.menu_mobile_alignment}
              >
                {site_settings[`footer_nav_${index}_title`]}
              </ButtonLabel>
            {/if}
            {#if site_settings[`footer_nav_${index}_description`]}
              <p
                class='footer-nav-group-description text-control text-2 leading-trim'
                style:text-align={site_settings.menu_mobile_alignment}
              >
                {@html render_micro_markdown(site_settings[`footer_nav_${index}_description`])}
              </p>
            {/if}
            <Navigation
              id={`mobile-footer-nav-${index}`}
              text_style='text-control'
              orientation='column'
              nav_items={site_settings[`footer_nav_${index}_items`]}
              current={current_href}
              align={site_settings.menu_mobile_alignment}
              text_size='text-large'
              class='full-width mobile-nav-section'
            />
          {/if}
        {/each}
      </section>

      {#if site_settings.menu_background !== 'transparent'}
        <div class='backdrop'></div>
        <svg
          class="svg-mask"
          width="100%"
          height="100%"
          preserveAspectRatio="none"
        >
          <mask id="milk_glass_mask">
            <rect
              class="header-rect"
              width="100%"
              height="100%"
              fill="white"
              style="{site_settings.menu_placement.includes('floating') 
                ? 'rx: calc(var(--dynamic-island-height) / 2); ry: calc(var(--dynamic-island-height) / 2);' 
                : ''}"
            />
          </mask>
        </svg>
      {/if}
    </div>
  </BlockGrid>

</header>

<svelte:window {onkeydown} />

{#if show_series_delete_dialog}
  <Dialog onclose={() => { show_series_delete_dialog = false; }}>
    <h2 class='text-heading3 mbe m-2'>{t('series_delete_confirm_title')}</h2>

    {#if is_loading_series_impact}
      <p class='text-secondary'>...</p>
    {:else if series_delete_impact}
      <p class='text-secondary mbe m-4' style='font-size: var(--font-size-reduce-1)'>
        {#if series_delete_impact.with_registrations_count > 0}
          {t('series_impact_summary', {
            total: series_delete_impact.total_future_count,
            with_reg: series_delete_impact.with_registrations_count,
            reg_count: series_delete_impact.total_registrations_count,
          })}
        {:else}
          {t('series_impact_no_registrations', {
            total: series_delete_impact.total_future_count,
          })}
        {/if}
      </p>

      {#if series_delete_impact.with_registrations_count > 0}
        <label class='flex-row gap-x g-2 items-center mbe m-4 text-control' style='font-size: var(--font-size-reduce-1)'>
          <input type='checkbox' bind:checked={series_delete_notify} />
          {t('notify_registrants_of_cancellation')}
        </label>
      {/if}

      <div class='flex-column gap-y g-2 pbs p-4' style='border-top: 1px solid var(--clr-border, #eee)'>
        <button
          disabled={is_series_deleting}
          onclick={() => handle_delete_series('all')}
        >
          <ButtonLabel variant={`brand ${site_settings.primary_control_surface}`}>
            {is_series_deleting ? '...' : t('delete_all_events')}
          </ButtonLabel>
        </button>

        {#if series_delete_impact.with_registrations_count > 0 && series_delete_impact.with_registrations_count < series_delete_impact.total_future_count}
          <button
            disabled={is_series_deleting}
            onclick={() => handle_delete_series('empty_only')}
          >
            <ButtonLabel variant={`brand ${site_settings.secondary_control_surface}`}>
              {t('delete_empty_only')}
            </ButtonLabel>
          </button>
        {/if}

        <button onclick={() => { show_series_delete_dialog = false; }}>
          <ButtonLabel variant={`brand ${site_settings.secondary_control_surface}`}>
            {t('cancel')}
          </ButtonLabel>
        </button>
      </div>
    {/if}
  </Dialog>
{/if}

<style lang='postcss'>
  .embed-create-menu {
    padding-block: var(--size-2);
    min-width: 14rem;
  }
  .embed-create-heading {
    margin: var(--size-2) 0 0;
    padding: var(--size-3) var(--size-4) var(--size-2);
    border-block-start: 1px solid var(--color-default-stroke);
  }
  /* The dynamic island row-1 height is calculated based on the max-size of the nav-items, the input-block-spacing and the button-outer-block-spacing. */
  :root {
    --dynamic-island-height: calc(
      max(
        calc(
          max(
            var(--menu-item-min-size), 
            var(--dynamic-island-max-nav-item-size)
            ) 
            + 2 * var(--default-button-outer-block-spacing)),
        var(--menu-item-min-size)
      )
    );
    /* in case of floating menus we need to add the block spacing used in the floating menu */
    --dynamic-island-bottom-edge: calc(var(--dynamic-island-height) + var(--dynamic-island-block-spacing, 0px));
  }
  :root:has(:is(.floating_flex_width, .floating_extended_1_width, .floating_extended_2_width)) {
    --dynamic-island-block-spacing: var(--size-3);
    --dynamic-island-border-radius: calc(var(--dynamic-island-height) / 2);
  }

  .dynamic-island {
    --dynamic-island-content-width: calc(100vw - 2 * var(--dynamic-island-outer-spacing-x, 0px));
    position: sticky;
    grid-area: dynamic-island;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    z-index: 9999;
    top: 0;
    left: 0;
    width: 100%;
    max-width: 100vw;
    transition-property: height, background-size;
    transition-duration: 700ms;
    transition-timing-function: var(--transition-spring-soft);
    > :global(*) {
      z-index: 9999;
    }

    .surface {
      display: grid;
      width: 100%;
      height: 100%;
      transition-property: height;
      transition-duration: 700ms;
      transition-timing-function: var(--transition-spring-soft);
      align-items: start;
      align-content: start;
      position: relative;
      .row-1 {
        container-type: inline-size;
      }
      .row-2 {
        grid-column: 1 / -1;
        grid-row: 2 / 3;
      }
    }

    &.has-background .surface {
      box-shadow: var(--dynamic-island-elevation);
      /* This is only relevant for non-opaque backgrounds */
      .backdrop, .svg-mask {
        position: absolute;
        inset: 0;
        z-index: -1;
        pointer-events: none;
      }
      .backdrop {
        backdrop-filter: var(--transluscent-filter);
        background: linear-gradient(
          to bottom,
          /* subtle gradient: reduce alpha for the bottom part, increase alpha for the top part (to avoid flickering & increase readability). */
          oklch(from var(--color-background, var(--color-default-background)) l c h / clamp(alpha * 1.5, alpha, alpha)) 0%,
          oklch(from var(--color-background, var(--color-default-background)) l c h / clamp(alpha * 0.5, alpha, alpha)) 50%
        );
        /* 
          Blurred glass not only renders colors from directly behind it, but also reflects colors from the adjacent sides.
          To recreate this effect we extend the height. The blur will now consider pixels outside the "visible" surface.
          Then we mask the added pixels with a svg mask, since we need to consider the border radius of the surface (floating variants).
          Technique derived from: https://www.joshwcomeau.com/css/backdrop-filter/
        */
        height: 200%; 
        mask-image: url(#milk_glass_mask); /** the mask covers 50% of the height, so we need to extend the height by 200% */
        pointer-events: none;
      }
    }

    &.has-background {
      &[class*="docked_"] .surface {
        .svg-mask {
          top: -10px;
          height: calc(100% + 10px);
        }
        .backdrop {
          top: -10px;
          height: calc(200% + 10px);
        }
      }
    }

    &.mobile-drawer-open .surface {
      /* height: 100dvh; */
    }

    /* Menu Variants */
    &.docked_extended_1_width.menu-variant, 
    &.docked_extended_2_width.menu-variant, 
    &.docked_flex_width.menu-variant {
      .surface {
        grid-template-columns: inherit;
        grid-column: 1 / -1;
        .row-1 {
          grid-column: var(--dynamic-island-track-start) / var(--dynamic-island-track-end);
          @media (max-width: 768px) {
            /* on mobile don't occupy inline-padding track */
            grid-column: 2 / -2;
          }
        }
      }
      :global(#topbar-nav .nav-group:first-child .nav-link:first-child .button-label) {
        /* align with left edige of main layout */
        padding-inline-start: 0;
      }
      .desktop-ellipsis-menu-button {
        padding-inline-end: 0;
      }
    }

    /* DOCKED */
    &.docked_extended_1_width.menu-variant,
    &.docked_extended_2_width.menu-variant {
      .row-1 {
        /* padding at edges of surface */
        @media (min-width: 769px) {
          /* padding-inline: var(--size-3); */
        }
      }
    }

    /* FLOATING */
    &.floating_extended_1_width.menu-variant,
    &.floating_extended_2_width.menu-variant,
    &.floating_flex_width.menu-variant {
      .surface {
        grid-column: var(--dynamic-island-track-start) / var(--dynamic-island-track-end);
        margin-block-start: var(--dynamic-island-block-spacing, 0px);
        border-radius: var(--dynamic-island-border-radius);
        height: fit-content;
        .row-1 {
          padding-inline-end: var(--size-2);
          :global(.nav-group:first-child .nav-item:first-child .button-label) {
            /* make sure the first nav item is not cropped by border radius
            set to half the height, which matches the radius of the dynamic island */
            padding-inline: calc(0.5 * var(--dynamic-island-height));
          }
        }
      }
      .mobile-drawer {
        /* border-radius: var(--dynamic-island-border-radius); */
      }
    }
  }

  /* Phones: entry and block tools share one strip that scrolls as a whole (save and cancel
     float at the bottom of the screen there), instead of two strips scrolling side by side */
  @media (max-width: 768px) {
    .dynamic-island.is-editing .row-1 {
      display: flex;
      overflow-x: auto;
      scrollbar-width: none;
      .row-1-left, .row-1-center {
        flex: none;
        width: auto;
        overflow: visible !important;
      }
    }
  }

  /* Page intro (StoryBlock, PAGE INTRO): when the site opens, the menu drops in from above just
     after the first title has begun. Only on the first page of a visit (.intro, see the script). Its
     rows move, not the bar: a bar with its own background would leave a hole in the page's
     colour while it faded in. Unlike the texts, without blur (as on tbc.co). */
  @keyframes intro-drop {
    from {
      opacity: 0;
      translate: 0 calc(-4rem * var(--animation-intensity, 1));
    }
  }
  @media (prefers-reduced-motion: no-preference) {
    :global(.layout-container:not(.animations-off)) .dynamic-island.intro .surface > :is(.row-1, .row-2) {
      animation: intro-drop var(--duration-reveal) var(--ease-settle) 300ms backwards;
    }
  }

  .dynamic-island.is-editing .row-1 {
    /* Editing puts up to ten tools in one row: they sit closer than navigation items */
    --island-button-outer-inline-spacing: var(--size-1);
    /* Entry tools (col 1) take only their own width (and scroll beyond 60%), the block tools
       follow right after them, and the free space sits in one place: before save/cancel.
       A 1fr entry column used to open a gap between the two groups that changed with the
       island width. */
    grid-template-columns: fit-content(60%) minmax(0, 1fr) auto auto;
    .row-1-center {
      justify-self: start;
      min-width: 0;
      overflow-x: auto;
      /* One rule between the entry tools and the block tools */
      &::before {
        content: '';
        flex: none;
        width: 1px;
        align-self: stretch;
        background-color: var(--color-default-foreground-800);
        margin-inline-end: var(--size-2);
      }
      > :global(*) {
        flex-shrink: 0;
        white-space: nowrap;
      }
    }
  }

  /* While editing, the tool strip (settings, replace, fit, viewport, …) can be
     wider than the island on narrow screens: scroll it horizontally instead of
     clipping or overlapping the save/cancel controls. Popovers use the top
     layer, so overflow does not clip them. Tools keep their natural width —
     labels must never wrap or hyphenate under squeeze. */
  /* Inside the island, buttons trade inner padding for outer spacing: the surface (or hover
     surface) hugs its text and neighbouring surfaces get a clear gap, so the row does not feel
     cramped. The outer spacing is a margin on the label, INSIDE the <a>/<button>, so the click
     area stays as large as before. A lone icon keeps the full padding - that is its tap width -
     and flush/underlined labels and menus opened from the island are left alone. */
  .dynamic-island :is(.row-1, .row-2) {
    --island-button-outer-inline-spacing: calc(var(--size-2) + var(--size-1));
    /* The spacing lives on the label here, not on the nav item. Segments in a button group
       stay flush: a margin there splits the control into separate boxes. */
    :global(.navigation.flex-row .nav-item) {
      --button-outer-inline-spacing: 0;
    }
    :global(.button-label:not(.flush, .underlined, .linkish, .tab, .tab-selected):not(.popover .button-label):not(.button-group .button-label)) {
      --button-label-padding-inline: calc(var(--input-min-height) / 4);
      margin-inline-start: var(--island-button-outer-inline-spacing-start, var(--island-button-outer-inline-spacing));
      margin-inline-end: var(--island-button-outer-inline-spacing-end, var(--island-button-outer-inline-spacing));
      :global(> :is(.start-icon-container, .end-icon-container):first-child:last-child) {
        --button-label-padding-inline: calc(var(--input-min-height) / 2 * 0.8);
      }
    }
    /* Same on the block axis: a slightly lower surface, the difference goes into a margin so
       the button keeps its full height. Image links size themselves by their image. */
    :global(.button-label:not(.flush, .underlined, .linkish, .tab, .tab-selected):not(.popover .button-label):not(.button-group .button-label):not(.image-only > .button-label)) {
      margin-block: var(--size-1);
      :global(> *) {
        min-height: calc(var(--input-min-height) - 2 * var(--size-1));
      }
    }
    /* Ghostly buttons carry no surface at rest: the padding only has to frame the hover surface */
    :global(.button-label.ghostly:not(.popover .button-label)) {
      --button-label-padding-inline: calc(var(--input-min-height) / 6);
    }
  }
  /* The first ghostly button on the left sits flush with the island's content edge, so its label
     starts exactly where the page content below starts: no start padding, no outer spacing. */
  .row-1-left > :global(:is(a, button):first-child),
  .row-1-left > :global(.navigation:first-child > .nav-group:first-child > .nav-item:first-child) {
    --button-outer-inline-spacing-start: 0;
    --island-button-outer-inline-spacing-start: 0;
    :global(> .button-label.ghostly) {
      --button-label-padding-inline-start: 0;
    }
  }

  .dynamic-island.is-editing .row-1 .row-1-left {
    min-width: 0;
    overflow-x: auto;

    > :global(*) {
      flex-shrink: 0;
      white-space: nowrap;
    }
  }
  .row-1 {
    display: grid;
    grid-template-columns: 1fr auto auto auto;
    width: 100%;
    align-items: center;
    justify-items: center;
    grid-template-areas: 
      'row-1-left row-1-center row-1-right hamburger-menu';
    .row-1-left,
    .row-1-center,
    .row-1-right {
      display: flex;
      position: relative;
      max-width: 100%;
      height: 100%;
      &:empty {
        display: none;
      }
    }
    .row-1-left,
    .row-1-center {
      align-items: center;
    }
    .row-1-center,
    .row-1-right {
      width: fit-content;
    }
    .row-1-left {
      width: 100%;
    }
    .row-1-right {
      align-items: stretch;
      padding-inline-start: var(--size-3);
    }
    .row-1-left-mobile {
      display: none;
      justify-self: flex-start;      
    }
    :global(#topbar-nav) {
      width: 100%;
      height: 100%;
    }
    /* The nav never wraps: the island has a fixed height, so a second line would spill over the
       page. When the items do not fit (narrow phones, enlarged text), the left side scrolls
       sideways instead (scrollbar hidden) and every label keeps its full text. */
    :global(#topbar-nav .nav-group) {
      flex-wrap: nowrap;
    }
    :global(#topbar-nav .nav-group > *) {
      flex-shrink: 0;
    }
    &:not(:has(.align-mobile-center)) {
      .row-1-left-mobile {
        :global(> *:first-child .button-label) {
          padding-inline-start: 0;
        }
      }
    }
  }
  /* Phones have no width to spare, so the spacing that stacks up goes: the first label drops its
     end spacing too (the next button brings its own), and the right group drops its start
     padding (the icon's tap box already keeps it clear). */
  @media (max-width: 768px) {
    .row-1-left > :global(:is(a, button):first-child),
    .row-1-left > :global(.navigation:first-child > .nav-group:first-child > .nav-item:first-child) {
      --island-button-outer-inline-spacing-end: 0;
    }
    .row-1 .row-1-right {
      padding-inline-start: 0;
    }
  }
  /* In docked islands the hamburger icon sits on the content edge, mirroring the first label on
     the left; its tap box reaches into the page gutter. */
  .dynamic-island:is(.docked_extended_1_width, .docked_extended_2_width, .docked_flex_width) .mobile-drawer-toggle :global(> .button-label) {
    margin-inline-end: calc((var(--icon-size) - var(--input-min-height)) / 2);
  }

  @keyframes enable-overflow {
    to {
      overflow-y: auto;
    }
  }

  /* mobile designs */  
  .mobile-drawer {
    --transition-duration: 700ms;
    /* --transition-timing: cubic-bezier(.4,0,.6,1); */
    --transition-timing: var(--transition-spring-overshoot);
    grid-column: 1 / -1;
    padding-inline: var(--page-inline-spacing);
    padding-block: 0;
    visibility: hidden;
    overflow-y: hidden;
    width: 100%;
    opacity: 0;
    height: 0px;
    /* Physical longhands: Safari does not animate padding-block when it is listed here, so the
       padding snapped to 0 on close and the content jumped up. */
    transition-property: height, opacity, visibility, padding-top, padding-bottom;
    transition-duration: var(--transition-duration);
    transition-timing-function: var(--transition-timing);
    /* z-index: 9999; */
    @starting-style {
      opacity: 0;
      height: 0px;
      padding-block: 0;
    }
    :global(> *) {
      width: 100%;
    }
    :global(.mobile-nav-section), :global(.nav-group) {
      display: contents;
    }
    :global(.footer-nav-group-title) {
      margin-block-start: var(--size-8);
    }
    :global(.footer-nav-group-description) {
      padding-block: var(--input-block-spacing);
      color: var(--text-2);
    }
    :global(.nav-item) {
      /* 💡 Insight: this type of staggered transition is not feasible with multiple nested nav groups. */
      /* We anyway should simplify the navigation to a single flat array of nav items. We already have concepts for separators. 
      We only need an optional "label" for separators or distinguish between divider and section header. */
      /* transition-property: transform, opacity;
      transition-timing-function: var(--transition-timing);
      transition-duration: calc(var(--index) * calc(var(--transition-duration) / 20));
      transform: translateY(calc(-0.5em - var(--index) * 0.3em));
      opacity: 0;
      will-change: transform, opacity; */
    }
    :global(.nav-item.has-encapuslated-surface) {
      /* add spacing so buttons frames don't touch each other. Half above, half below, so a
         frame sits evenly between the items around it. */
      padding-block: calc(var(--size-3) / 2);
    }
    /* Start/end aligned: every text sits on the page's content edge. Ghostly labels drop their
       padding on that side; a label with a surface hangs its padding into the gutter, so its
       text lines up with the others, not its frame. */
    &.items-start :global(.button-label.ghostly) {
      --button-label-padding-inline-start: 0;
    }
    &.items-end :global(.button-label.ghostly) {
      --button-label-padding-inline-end: 0;
    }
    &:is(.items-start, .items-end) :global(.nav-item.has-encapuslated-surface) {
      --button-outer-inline-spacing: 0;
      :global(> .button-label) {
        max-width: none;
        width: calc(100% + 2 * var(--button-label-padding-inline));
        margin-inline: calc(-1 * var(--button-label-padding-inline));
      }
    }
    /* Hide first nav-group if it doesn't have a second nav-item */
    :global(#mobile-nav .nav-group:first-child:not(:has(.nav-item:nth-child(2)))) {
      display: none;
    }
    /* Hide first nav-item, but only if there's only one nav-group */
    :global(#mobile-nav:not(:has(.nav-group:nth-child(2))) .nav-group:first-child .nav-item:first-child:not(:has(.nav-item:nth-child(2)))) {
      display: none;
    }
    &.open {
      visibility: visible;
      opacity: 1;
      height: calc(100dvh - var(--dynamic-island-bottom-edge) - 2 * var(--dynamic-island-block-spacing, 0px));
      padding-block: var(--mobile-drawer-padding-block, var(--dynamic-island-border-radius, var(--size-6)));
      /* delay overflow-y: auto to prevent scrollbar flickering */
      animation: enable-overflow 0s linear var(--transition-duration) forwards;
      :global(.nav-item){
        /* transform: translateY(0);
        opacity: 1; */
      }
    }
    :global(.login-link) {
      align-self: flex-start;
    }
  }

  /* hide on mobile */
  .dynamic-island .desktop-ellipsis-menu-button,
  .dynamic-island .row-1-right :global(.desktop-start-editing-tool),
  .dynamic-island .row-1-right :global(.cancel-tool),
  .dynamic-island .row-1-right :global(.save-tool) {
    display: none;
    /* @media (min-width: 1025px) {
      display: flex;
    } */
  }
  
  .dynamic-island :global(#topbar-nav.align-mobile-start) {
    justify-content: flex-start;
  }
  /* `safe`: when the nav is wider than the row (it scrolls then), it starts at the start edge
     instead of overflowing on both sides, where the start would be out of reach. */
  .dynamic-island:not(:has(.row-1-right *)) :global(#topbar-nav.align-mobile-center) {
    justify-content: safe center;
    width: 100%;
    /* counter balance the hamburger icon and its outer-spacing */
    /* @media (max-width: 1024px) {
      padding-inline-start: calc(var(--input-min-height) + var(--size-2));
      justify-content: center;
      :global(.nav-group) {
        justify-content: center;
        flex-grow: 1;
        :global(.nav-link .button-label) {
          padding-inline-start: var(--input-inline-spacing);
        }
      }
    } */
  }
  .dynamic-island :global(#topbar-nav.align-mobile-end) {
    justify-content: safe flex-end;
  }

  .mobile-ellipsis-menu-button,
  .mobile-drawer-toggle {
    display: flex;
    justify-self: flex-end;
    /* @media (min-width: 1025px) {
      display: none;
    } */
  }

  /* The row fades out while the drawer opens and back in while it closes. The transition sits on
     the resting state so it also runs on close; editing keeps its own timing. */
  .dynamic-island:not(.is-editing) .row-1 > :is(.row-1-left, .row-1-center, .row-1-right) {
    transition: opacity 300ms cubic-bezier(.4,0,.6,1);
  }
  .mobile-drawer-open {
    .row-1-left,
    .row-1-center,
    .row-1-right {
      opacity: 0;
      pointer-events: none;
    }
    .row-1-left-mobile {
      display: flex;
      animation: fade-in 300ms cubic-bezier(.4,0,.6,1);
    }
  }

  /* Hide editing toolbar when contextual drawer is open */
  .contextual-editing-active {
    .row-1-center,
    .row-1-right .save-cancel-tool {
      opacity: 0;
      transition: opacity 0.15s ease-out;
      pointer-events: none;
    }
  }
  
  .floating_extended_1_width.menu-variant, 
  .floating_extended_2_width.menu-variant, 
  .floating_flex_width.menu-variant,
  .mobile-entry-editing-tools {      
    :global(.cancel-tool .button-label) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    :global(.save-tool .button-label) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
    :global(.save-tool) {        
      --button-outer-inline-spacing: 0 !important;
    }
    :global(.cancel-tool) {
      --button-outer-inline-spacing: 0 !important;
    }
  }

  .mobile-entry-editing-tools {
    padding-inline: var(--size-2);
    position: absolute;
    /* Contextual editors live in a shorter drawer that clips viewport-height positioning. */
    top: calc(var(--drawer-height, 100dvh) - var(--dynamic-island-block-spacing, var(--size-4)));
    left: 50%;
    transform: translateX(-50%) translateY(-100%);
    z-index: 9999;
    box-shadow:var(--dynamic-island-shadow, var(--shadow-md));
    /* background-color: color-mix(in oklch, var(--color-background, var(--color-default-background)) 90%, oklch(from var(--color-default-background) 0 0 h / 0) 10%); */
    background-color: oklch(from var(--color-background, var(--color-default-background)) l c h / 0.9);
    backdrop-filter: var(--transluscent-filter);
    border-radius: var(--radius-md);
    transition: transform 200ms cubic-bezier(.66,0,.08,1) 100ms;
    /* @media (min-width: 1025px) {
      display: none;
    } */
    border-radius: var(--input-border-radius);
    &.mobile-drawer-open {
      transform: translateY(30vh) scale(0.6);
    }
  }


  /* When scrolling to any item (e.g. via click) add a scroll margin equivalent to the height od the dynamic island */
  :global(.main-page-content *:focus-visible) {
    scroll-margin-top: calc(var(--dynamic-island-bottom-edge) + var(--size-4) * 2);
  }

  .dynamic-island-backdrop {
    background-color: oklch(from var(--color-background, var(--color-default-background)) l c h / 0.4);
    visibility: hidden;
    position: fixed;
    opacity: 0;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 9989;
    backdrop-filter: blur(20px);
    transition-duration: 300ms;
    transition-property: opacity, visibility;
    transition-timing-function: cubic-bezier(.4,0,.6,1);
    @starting-style {
      opacity: 0;
      visibility: hidden;
    }
    &.visible {
      opacity: 1;
      visibility: visible;
    }
  }


  .logo-nav-item {
    :global(.button-label) {
      border-radius: 0;
    }
    :global(.button-label .media-item) {
      /* prevent logo from overflowing */
      max-width: 100%;
      width: fit-content;
      min-width: 40px;
    }
  }

  .row-2{
    width: 100%;
    &:empty {
      display: none;
    }
  }

  :global(.footer-nav-group-title .button-text) {
    --button-text-color: var(--color-foreground, var(--color-default-foreground));
  }
  @media (max-width: 768px) {
    .explore-link :global(.button-text) {
      display: none;
    }
  }
</style>

